/**
 * Subscribes to Supabase Realtime changes on key tables
 * and invalidates corresponding React Query caches automatically.
 *
 * Mount once inside the authenticated shell so every page
 * benefits from live sync across tabs / devices / PWA.
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** Tables we listen to and the query-key prefixes they invalidate */
const TABLE_INVALIDATION_MAP: Record<string, string[]> = {
  balance_entries: ["balance_entries"],
  etat_financier_data: ["etat_financier_data"],
  dossiers: ["dossiers", "dossier"],
  mapping_comptes: ["mapping_comptes"],
  pointage_results: ["pointage_results"],
  custom_formulas: ["custom_formulas"],
};

export function useRealtimeSync() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("global-sync")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "balance_entries" },
        () => invalidate("balance_entries"),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "etat_financier_data" },
        () => invalidate("etat_financier_data"),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "dossiers" },
        () => invalidate("dossiers"),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "mapping_comptes" },
        () => invalidate("mapping_comptes"),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "pointage_results" },
        () => invalidate("pointage_results"),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "custom_formulas" },
        () => invalidate("custom_formulas"),
      )
      .subscribe();

    function invalidate(table: string) {
      const prefixes = TABLE_INVALIDATION_MAP[table] ?? [table];
      for (const prefix of prefixes) {
        queryClient.invalidateQueries({ queryKey: [prefix] });
      }
    }

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
}
